/* @flow */
import invariant from 'invariant';
import * as React from 'react';

import Glyph from 'components/Glyph';
import type { GlyphSize } from 'components/Glyph';
import Menu from 'components/Menu';
import MenuButton from 'components/MenuButton';
import MenuDivider from 'components/MenuDivider';
import ModalMenuItem from 'components/ModalMenuItem';
import Group from 'entities/Group';
import AddGroupOwnerModalConfirm from 'groups/components/AddGroupOwnerModalConfirm';
import RemoveMemberMenuItem from 'groups/components/RemoveMemberMenuItem';
import RemoveFromOrgModal from 'organizations/components/RemoveFromOrgModal';
import ReportOrgUserModal from 'organizations/components/ReportOrgUserModal';
import { BatchRoleCorrectionModalCommon } from 'users/components/BatchRoleCorrectionModal';
import BlockMemberMenuItem from 'users/components/BlockMemberMenuItem';
import GetTranscriptMenuItem from 'user/components/GetTranscriptMenuItem';
import RelationshipModal from 'user/components/RelationshipModal';
import ReportUserMenuItem from 'user/components/ReportUserMenuItem';
import SubscribeUserMenuItem from 'user/components/SubscribeUserMenuItem';

import type { UserData as GetTranscriptUserData } from 'user/components/GetTranscriptMenuItem';
import type { PotentialRole } from 'groups/components/OrgRoleDropdown';
import type { NewUserProps as RelationshipModalUser } from 'user/components/RelationshipViewHeader';
import type { OrganizationRole } from 'queries/__generated__/globalGQLTypes';

export type UserActionsMenuButtonUser = {
  id: Backbone$Id,
  uuid: string,
  name: string,
  first_name: string,
  last_name: string,
  blocked: boolean,
  can_have_role_changed: boolean,
  roles: OrganizationRole[],
  potential_roles: PotentialRole[],

  is_owner: boolean,
  can_become_owner: boolean,
  can_admin: boolean,
  can_remove_from_group: boolean,
  can_remove_from_org: boolean,
} & GetTranscriptUserData &
  RelationshipModalUser;

type Props = {
  buttonSize?: string,
  className?: string,
  copyToClassText?: string,
  disableBlockMember?: boolean,
  disableChangeRole?: boolean,
  disableCopyToClass?: boolean,
  disabled?: ?boolean,
  disableGetTranscript?: boolean,
  disableMakeOwner?: boolean,
  disableRemoveFromSchool?: boolean,
  disableRemoveMember?: boolean,
  disableReportAsAdmin?: boolean,
  disableReportAsUser?: boolean,
  disableViewProfile?: boolean,
  glyphSize?: GlyphSize,
  groupToAdmin?: Group,
  organization?: {
    +id: Backbone$Id,
    +name: string,
  },
  handleClick?: Function,
  isAdminView?: ?boolean,
  level?: string,
  onUserRemoved?: () => any,
  onUserUpdate?: () => any,
  onGetTranscript?: (requested: boolean) => void,
  showBlockMember?: boolean,
  showChangeRole?: boolean,
  showCopyToClass?: boolean,
  showGetTranscript?: boolean,
  showMakeOwner?: boolean,
  showRemoveFromSchool?: boolean,
  showRemoveMember?: boolean,
  showReportAsAdmin?: boolean,
  showReportAsUser?: boolean,
  showViewProfile?: boolean,
  user: UserActionsMenuButtonUser,
  tooltip?: string,
  tooltipPlacement?: 'top' | 'right' | 'bottom' | 'left',
};

class UserActionsMenuButton extends React.Component<Props> {
  static defaultProps = {
    buttonSize: 'xsmall',
    glyphSize: 'xlarge',
    level: 'secondary',
  };

  handleUserRemoved = () => {
    const { onUserRemoved } = this.props;

    if (onUserRemoved) {
      onUserRemoved();
    }
  };

  handleUserUpdate = () => {
    const { onUserUpdate } = this.props;

    if (onUserUpdate) {
      onUserUpdate();
    }
  };

  renderMakeOwnerDialog = () => {
    const { groupToAdmin, user } = this.props;
    invariant(groupToAdmin, 'renderMakeOwnerDialog called without a group');
    invariant(user, 'renderMakeOwnerDialog called without a user');

    return (
      <AddGroupOwnerModalConfirm
        group={groupToAdmin}
        checkedIds={[user.id]}
        userName={formatNameForActionsMenuButton(user)}
      />
    );
  };

  renderChangeRoleModal = () => {
    const { user } = this.props;
    invariant(user, 'renderChangeRoleModal called without a user');

    const userData = [
      {
        user,
        orgAffiliationView: {
          can_have_role_changed: user.can_have_role_changed,
          roles: user.roles,
          potential_roles: user.potential_roles,
        },
      },
    ];

    return (
      <BatchRoleCorrectionModalCommon
        organizationId={parseInt(this.orgFromProps(this.props, 'renderChangeRoleModal').id, 10)}
        userData={userData}
        onUserRoleSave={this.handleUserUpdate}
      />
    );
  };

  // FIXME: There's currently two ways to pass an org into this component
  //
  // Legacy: pass a groupToAdmin with an organization in it
  // New: pass an organization separately
  //
  // The problem with switching over to the new one entirely, is RelationshipModal
  //   implicitly relies on groupToAdmin having an organization, since eventually
  //   down the line it creates a UserActionsMenuButton, which used to rely on the
  //   org in groupToAdmin for various actions.
  // The solution would be making the RelationshipModal tree pass both an organization
  //   and a group to its UserActionsMenuButton, then change all users of
  //   UserActionsMenuButton to pass organizations seperately.
  orgFromProps(props: Props, fieldName: string) {
    const { organization, groupToAdmin } = props;

    // If we have an explicit organization, just use that
    if (organization) {
      return organization;
    }

    // Otherwise try and grab the one in the groupToAdmin (legacy)
    invariant(groupToAdmin, `${fieldName} called without a group`);
    const org = groupToAdmin.getOrganization();
    invariant(org, `${fieldName} called without a group associated to an org`);
    return org.toJSON();
  }

  renderMenu = () => {
    const {
      copyToClassText,
      disableBlockMember,
      disableChangeRole,
      disableCopyToClass,
      disableGetTranscript,
      disableMakeOwner,
      disableRemoveFromSchool,
      disableRemoveMember,
      disableReportAsAdmin,
      disableReportAsUser,
      disableViewProfile,
      groupToAdmin,
      organization,
      isAdminView,
      onUserRemoved,
      onGetTranscript,
      showBlockMember,
      showChangeRole,
      showCopyToClass,
      showGetTranscript,
      showMakeOwner,
      showRemoveFromSchool,
      showRemoveMember,
      showReportAsAdmin,
      showReportAsUser,
      showViewProfile,
      user,
    } = this.props;

    const hasItemsAboveFirstDivider = showChangeRole || showGetTranscript || showCopyToClass;
    const hasItemsBelowFirstDivider =
      showRemoveMember || showBlockMember || showReportAsUser || showReportAsAdmin;

    return (
      <Menu>
        {showViewProfile && (
          <ModalMenuItem
            eventElementName="user_action_menu_view_profile"
            modal={this.renderRelationshipDialog()}
            disabled={disableViewProfile}>
            <Glyph name="avatar" />
            <span>View profile</span>
          </ModalMenuItem>
        )}
        {showChangeRole && (
          <ModalMenuItem
            eventElementName="user_action_menu_change_role"
            modal={this.renderChangeRoleModal()}
            disabled={disableChangeRole}>
            <Glyph name="pencil-stroke" />
            <span>Change role</span>
          </ModalMenuItem>
        )}
        {showGetTranscript && (organization || groupToAdmin) && (
          <GetTranscriptMenuItem
            eventElementName="user_action_menu_get_transcript"
            disabled={disableGetTranscript}
            user={user}
            organization={this.orgFromProps(this.props, 'GetTranscriptMenuItem')}
            isAdminView={isAdminView}
            onGetTranscript={onGetTranscript}
          />
        )}
        {showCopyToClass && (
          <SubscribeUserMenuItem
            subscriberId={parseInt(user.id, 10)}
            disabled={!!disableCopyToClass}
            eventElementName="user_action_menu_copy_to_class">
            {copyToClassText}
          </SubscribeUserMenuItem>
        )}
        {showMakeOwner && (
          <ModalMenuItem
            eventElementName="user_action_menu_make_owner"
            modal={this.renderMakeOwnerDialog()}
            disabled={disableMakeOwner}>
            <Glyph name="crown-stroke" />
            <span>Make owner of class</span>
          </ModalMenuItem>
        )}
        {hasItemsAboveFirstDivider && hasItemsBelowFirstDivider && <MenuDivider />}
        {showRemoveMember && (
          <RemoveMemberMenuItem
            eventElementName="user_action_menu_remove_member"
            disabled={disableRemoveMember}
            group={groupToAdmin}
            user={user}
            onRemove={onUserRemoved}
          />
        )}
        {showBlockMember && (
          <BlockMemberMenuItem
            disabled={disableBlockMember}
            blocked={user.blocked}
            memberId={parseInt(user.id, 10)}
            memberName={formatNameForActionsMenuButton(user)}
          />
        )}
        {showReportAsUser && (
          <ReportUserMenuItem
            reportee={user}
            disabled={!!disableReportAsUser}
            eventElementName="user_action_menu_report_as_user"
          />
        )}
        {showReportAsAdmin && (
          <ModalMenuItem
            disabled={disableReportAsAdmin}
            eventElementName="user_action_menu_report_as_admin"
            modal={this.renderReportOrgUserModal()}>
            <Glyph name="flag" />
            <span>Report to Remind</span>
          </ModalMenuItem>
        )}
        {showRemoveFromSchool && (organization || groupToAdmin) && (
          <React.Fragment>
            <MenuDivider />
            <ModalMenuItem
              eventElementName="user_action_menu_remove_from_school"
              modal={this.renderRemoveFromOrgModal()}
              disabled={disableRemoveFromSchool}>
              <Glyph name="close" />
              <span>Remove from school</span>
            </ModalMenuItem>
          </React.Fragment>
        )}
      </Menu>
    );
  };

  renderRelationshipDialog = () => {
    const { groupToAdmin, user } = this.props;
    invariant(user, 'renderRelationshipDialog called without a user');

    return <RelationshipModal groupToAdmin={groupToAdmin} userId={user.id} user={user} />;
  };

  renderRemoveFromOrgModal = () => {
    const { user, isAdminView } = this.props;

    const org = this.orgFromProps(this.props, 'renderRemoveFromOrgModal');
    invariant(user, 'renderRemoveFromOrgModal called without a user');

    return (
      <RemoveFromOrgModal
        isAdminView={isAdminView}
        userName={formatNameForActionsMenuButton(user)}
        userIds={[user.id]}
        orgName={org.name}
        orgId={org.id}
        reportModal={this.renderReportOrgUserModal}
        onRemoved={this.handleUserRemoved}
      />
    );
  };

  renderReportOrgUserModal = () => {
    const { user } = this.props;

    const org = this.orgFromProps(this.props, 'renderReportOrgUserModal');
    invariant(user, 'renderReportOrgUserModal called without a user');

    return (
      <ReportOrgUserModal
        userName={formatNameForActionsMenuButton(user)}
        userId={user.id}
        orgName={org.name}
        orgId={org.id}
        removeModal={this.renderRemoveFromOrgModal}
      />
    );
  };

  handleClick = (event: SyntheticMouseEvent<>) => {
    event.stopPropagation();

    if (this.props.handleClick) {
      this.props.handleClick(event);
    }
  };

  render() {
    const {
      buttonSize,
      className,
      disabled,
      glyphSize,
      level,
      tooltip,
      tooltipPlacement,
    } = this.props;

    return (
      <MenuButton
        alignment="right"
        disabled={disabled}
        level={level}
        menu={this.renderMenu()}
        size={buttonSize}
        tooltip={tooltip}
        tooltipPlacement={tooltipPlacement}
        className={className}
        data-test-selector="user-actions-menu-button"
        onClick={this.handleClick}>
        <Glyph name="ellipses" size={glyphSize} solo />
      </MenuButton>
    );
  }
}

export function formatNameForActionsMenuButton(user: Object): string {
  const firstName = user.first_name || user.firstName;
  const lastName = user.last_name || user.lastName;
  return firstName && lastName ? `${firstName} ${lastName}` : user.name || '';
}

export default UserActionsMenuButton;
